import React, { useEffect, useState, createContext, useContext } from 'react';
import { mockLocations, mockClassTypes, mockSessions, mockBookings } from '../admin/mockData';
// Bump this version whenever mock data changes to force refresh
const DATA_VERSION = '2';
interface AdminDataContextType {
  locations: any[];
  classTypes: any[];
  sessions: any[];
  bookings: any[];
  saveLocation: (location: any) => void;
  deleteLocation: (id: string) => void;
  toggleLocationActive: (id: string) => void;
  saveClassType: (classType: any) => void;
  deleteClassType: (id: string) => void;
  saveSession: (session: any) => void;
  deleteSession: (id: string) => void;
  saveBooking: (booking: any) => void;
  updateBookingStatus: (id: string, status: string) => void;
}
const AdminDataContext = createContext<AdminDataContextType | undefined>(undefined);
function loadData(key: string, fallback: any) {
  // Check if data version matches; if not, clear stale data
  const savedVersion = localStorage.getItem('vimutti_data_version');
  if (savedVersion !== DATA_VERSION) {
    localStorage.removeItem('vimutti_locations');
    localStorage.removeItem('vimutti_class_types');
    localStorage.removeItem('vimutti_sessions');
    localStorage.removeItem('vimutti_bookings');
    localStorage.setItem('vimutti_data_version', DATA_VERSION);
    return fallback;
  }
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return fallback;
    }
  }
  return fallback;
}
export function AdminDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [locations, setLocations] = useState<any[]>(() => loadData('vimutti_locations', mockLocations));
  const [classTypes, setClassTypes] = useState<any[]>(() => loadData('vimutti_class_types', mockClassTypes));
  const [sessions, setSessions] = useState<any[]>(() => loadData('vimutti_sessions', mockSessions));
  const [bookings, setBookings] = useState<any[]>(() => loadData('vimutti_bookings', mockBookings));
  useEffect(() => {
    localStorage.setItem('vimutti_locations', JSON.stringify(locations));
  }, [locations]);
  useEffect(() => {
    localStorage.setItem('vimutti_class_types', JSON.stringify(classTypes));
  }, [classTypes]);
  useEffect(() => {
    localStorage.setItem('vimutti_sessions', JSON.stringify(sessions));
  }, [sessions]);
  useEffect(() => {
    localStorage.setItem('vimutti_bookings', JSON.stringify(bookings));
  }, [bookings]);
  // --- Locations ---
  const saveLocation = (data: any) => {
    if (data.id) {
      setLocations(prev => prev.map(loc => loc.id === data.id ? {
        ...loc,
        ...data
      } : loc));
    } else {
      setLocations(prev => [...prev, {
        ...data,
        id: Math.random().toString(36).substring(2, 9)
      }]);
    }
  };
  const deleteLocation = (id: string) => setLocations(prev => prev.filter(loc => loc.id !== id));
  const toggleLocationActive = (id: string) => {
    setLocations(prev => prev.map(loc => loc.id === id ? {
      ...loc,
      is_active: !loc.is_active
    } : loc));
  };
  // --- Class Types ---
  const saveClassType = (data: any) => {
    if (data.id) {
      setClassTypes(prev => prev.map(ct => ct.id === data.id ? {
        ...ct,
        ...data
      } : ct));
    } else {
      setClassTypes(prev => [...prev, {
        ...data,
        id: Math.random().toString(36).substring(2, 9)
      }]);
    }
  };
  const deleteClassType = (id: string) => setClassTypes(prev => prev.filter(ct => ct.id !== id));
  // --- Sessions ---
  const saveSession = (data: any) => {
    if (data.id) {
      setSessions(prev => prev.map(s => s.id === data.id ? {
        ...s,
        ...data
      } : s));
    } else {
      setSessions(prev => [...prev, {
        ...data,
        id: Math.random().toString(36).substring(2, 9),
        spots_available: data.capacity,
        status: 'active'
      }]);
    }
  };
  const deleteSession = (id: string) => setSessions(prev => prev.filter(s => s.id !== id));
  // --- Bookings ---
  const saveBooking = (data: any) => {
    if (data.id) {
      setBookings(prev => prev.map(b => b.id === data.id ? {
        ...b,
        ...data
      } : b));
    } else {
      setBookings(prev => [...prev, {
        ...data,
        id: Math.random().toString(36).substring(2, 9)
      }]);
    }
  };
  const updateBookingStatus = (id: string, status: string) => {
    setBookings(prev => prev.map(b => b.id === id ? {
      ...b,
      booking_status: status
    } : b));
  };
  return <AdminDataContext.Provider value={{
    locations,
    classTypes,
    sessions,
    bookings,
    saveLocation,
    deleteLocation,
    toggleLocationActive,
    saveClassType,
    deleteClassType,
    saveSession,
    deleteSession,
    saveBooking,
    updateBookingStatus
  }} data-id="element-1168">
      {children}
    </AdminDataContext.Provider>;
}
export function useAdminData() {
  const context = useContext(AdminDataContext);
  if (context === undefined) {
    throw new Error('useAdminData must be used within an AdminDataProvider');
  }
  return context;
}